import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiValidation, ConfirmDialogComponent } from 'src/app/@shared';
import { DEFAULT_SNACKBAR_CONFIG } from 'src/app/@shared/constants/site.constants';
import {
  buildClientKey,
  buildOrderedCategoryIds,
  DISPLAY_COLORS,
  DisplayColor,
  getNextCategoryDisplayOrder,
  readCategoryDisplayColor,
  StoreAttributeCategory,
  StoreAttributeCategoryViewModel,
} from '../../../../models/store-attribute.model';
import { StoreAttributeService } from '../../../../services/store-attribute.service';

export interface AddEditCategoryDialogData {
  category?: StoreAttributeCategoryViewModel;
  existingCategories?: StoreAttributeCategoryViewModel[];
}

type ApiErrorContext = 'save' | 'delete' | 'reorder';

@Component({
  selector: 'app-add-edit-category-dialog',
  templateUrl: './add-edit-category-dialog.component.html',
  styleUrls: ['./add-edit-category-dialog.component.scss'],
})
export class AddEditCategoryDialogComponent implements OnInit {
  formGroup!: FormGroup;
  readonly displayColors = DISPLAY_COLORS;
  isEditMode = false;
  isSaving = false;

  private readonly errorSnackBarConfig = {
    verticalPosition: 'top' as const,
    panelClass: ['snackbar-error'],
  };

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<AddEditCategoryDialogComponent>,
    private storeAttributeService: StoreAttributeService,
    private dialog: MatDialog,
    private matSnackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: AddEditCategoryDialogData,
  ) {}

  ngOnInit(): void {
    this.isEditMode = !!this.data.category;
    const category = this.data.category?.category;

    this.formGroup = this.formBuilder.group({
      CategoryName: [category?.CategoryName ?? '', Validators.required],
      DisplayColor: [
        (readCategoryDisplayColor(category ?? {}) as DisplayColor) ??
          this.data.category?.color ??
          'blue',
        Validators.required,
      ],
      Description: [category?.Description ?? ''],
    });
  }

  get title(): string {
    if (this.isEditMode) {
      return `Edit Category - ${this.data.category?.name ?? ''}`;
    }
    return 'Add Attribute Category';
  }

  get attributeTypeCount(): number {
    return this.data.category?.attributeTypes.length ?? 0;
  }

  get attributeTypeNames(): string {
    const types = this.data.category?.attributeTypes ?? [];
    if (!types.length) {
      return 'no attribute types yet';
    }
    return types.map((type) => type.name).join(', ');
  }

  get saveButtonLabel(): string {
    return this.isEditMode ? 'Save Changes' : 'Create Category';
  }

  selectColor(color: DisplayColor): void {
    this.formGroup.patchValue({ DisplayColor: color });
  }

  isColorSelected(color: DisplayColor): boolean {
    return this.formGroup.get('DisplayColor')?.value === color;
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }

  onSave(): void {
    if (this.formGroup.invalid || this.isSaving) {
      this.formGroup.markAllAsTouched();
      return;
    }

    const formValue = this.formGroup.getRawValue();
    const existingCategories = this.getExistingApiCategories();
    const payload: StoreAttributeCategory = {
      CategoryName: formValue.CategoryName.trim(),
      Description: formValue.Description?.trim() ?? '',
      DisplayColor: formValue.DisplayColor,
      DisplayOrder: this.isEditMode
        ? this.data.category!.category.DisplayOrder
        : getNextCategoryDisplayOrder(existingCategories),
      ClientKey: this.isEditMode
        ? this.data.category!.category.ClientKey ?? buildClientKey(formValue.CategoryName)
        : buildClientKey(formValue.CategoryName),
    };

    this.isSaving = true;
    const save$ = this.isEditMode
      ? this.storeAttributeService.updateCategory(this.data.category!.id, {
          ...this.data.category!.category,
          ...payload,
        })
      : this.storeAttributeService.createCategory(payload);

    save$.subscribe({
      next: (saved) => {
        if (this.isEditMode) {
          this.isSaving = false;
          this.storeAttributeService.reload();
          this.matSnackBar.open('Category updated', 'OK', DEFAULT_SNACKBAR_CONFIG);
          this.dialogRef.close(true);
          return;
        }

        const categoryId = saved.Id;
        if (!categoryId) {
          this.isSaving = false;
          this.matSnackBar.open('Category id missing from save response', 'Error', this.errorSnackBarConfig);
          return;
        }

        const orderedIds = buildOrderedCategoryIds(
          existingCategories,
          categoryId,
          existingCategories.length,
        );

        this.storeAttributeService.reorderCategories(orderedIds).subscribe({
          next: () => {
            this.isSaving = false;
            this.storeAttributeService.reload();
            this.matSnackBar.open('Category created', 'OK', DEFAULT_SNACKBAR_CONFIG);
            this.dialogRef.close(true);
          },
          error: (error) => {
            this.isSaving = false;
            this.handleApiError(error, 'reorder');
            this.storeAttributeService.reload();
          },
        });
      },
      error: (error) => {
        this.isSaving = false;
        this.handleApiError(error, 'save');
      },
    });
  }

  onDelete(): void {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirm Delete',
        message:
          'Deleting a category will also remove all attribute types and values within it. This action cannot be undone.',
      },
      disableClose: true,
    });

    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.storeAttributeService.deleteCategory(this.data.category!.id).subscribe({
          next: () => {
            this.storeAttributeService.reload();
            this.matSnackBar.open('Category deleted', 'OK', DEFAULT_SNACKBAR_CONFIG);
            this.dialogRef.close(true);
          },
          error: (error) => this.handleApiError(error, 'delete'),
        });
      }
    });
  }

  private getExistingApiCategories(): StoreAttributeCategory[] {
    return (this.data.existingCategories ?? []).map((vm) => vm.category);
  }

  private handleApiError(error: HttpErrorResponse, context: ApiErrorContext): void {
    if (error.status === 500) {
      const message =
        context === 'delete'
          ? 'Error deleting category'
          : context === 'reorder'
            ? 'Error reordering categories'
            : 'Error saving category';
      this.matSnackBar.open(message, 'Error', this.errorSnackBarConfig);
      return;
    }

    if (error.status === 400) {
      this.handleBadRequest(error);
      return;
    }

    if (error.status === 429) {
      this.matSnackBar.open(
        `Too many requests. Please wait a moment and retry. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    if (error.status === 504 || error.status === 408) {
      this.matSnackBar.open(
        `Server is taking too long to respond. Please try again. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    if (error.status === 0) {
      this.matSnackBar.open(
        `Request timed out or network issue. Check your connection. (statusCode: ${error.status})`,
        'Error',
        this.errorSnackBarConfig,
      );
      return;
    }

    const message =
      error?.error?.message ||
      error?.message ||
      `Unexpected error. (statusCode: ${error.status})`;
    this.matSnackBar.open(message, 'Error', this.errorSnackBarConfig);
  }

  private handleBadRequest(error: HttpErrorResponse): void {
    const apiValidations = error.error;

    if (Array.isArray(apiValidations) && typeof apiValidations[0] === 'string') {
      this.matSnackBar.open(apiValidations[0], 'Error', this.errorSnackBarConfig);
      return;
    }

    if (Array.isArray(apiValidations)) {
      const errorMessages: string[] = [];
      apiValidations.forEach((validation: ApiValidation) => {
        if (!validation?.PropertyName) {
          if (validation?.ErrorMessage) {
            errorMessages.push(validation.ErrorMessage);
          }
          return;
        }

        const control = this.resolveFormControl(validation.PropertyName);
        if (control) {
          control.markAsTouched();
          control.setErrors({
            ...(control.errors || {}),
            apiError: validation.ErrorMessage,
          });
        } else if (validation.ErrorMessage) {
          errorMessages.push(validation.ErrorMessage);
        }
      });

      if (errorMessages.length > 0) {
        this.matSnackBar.open(errorMessages[0], 'Error', this.errorSnackBarConfig);
      }
      this.formGroup.updateValueAndValidity();
      return;
    }

    if (apiValidations?.error?.message) {
      this.matSnackBar.open(apiValidations.error.message, 'Error', this.errorSnackBarConfig);
      return;
    }

    if (apiValidations?.message) {
      this.matSnackBar.open(apiValidations.message, 'Error', this.errorSnackBarConfig);
      return;
    }

    if (typeof apiValidations === 'string') {
      this.matSnackBar.open(apiValidations, 'Error', this.errorSnackBarConfig);
      return;
    }

    const fallbackMessage =
      typeof apiValidations === 'object' && apiValidations?.Message
        ? apiValidations.Message
        : 'Validation error';
    this.matSnackBar.open(fallbackMessage, 'Error', this.errorSnackBarConfig);
  }

  private resolveFormControl(propertyName: string) {
    const propertyMap: Record<string, string> = {
      CategoryName: 'CategoryName',
      Description: 'Description',
      DisplayColor: 'DisplayColor',
    };

    const controlName = propertyMap[propertyName] ?? propertyName;
    return this.formGroup.get(controlName);
  }
}
